import { MarkdownPreview } from '@shared/markdown';
import { DateTime } from 'luxon';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { LiaTimesSolid } from 'react-icons/lia';

import { TherapeuticClassReportItem } from '@/api/use-therapeutic-class-report';
import Nullish from '@/components/nullish';
import { Button } from '@/components-new/button';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '@/components-new/description-list';
import { Drawer, DrawerBody, DrawerHeader, DrawerTitle } from '@/components-new/drawer';
import { SectionHeading } from '@/components-new/section';
import { TextLink } from '@/components-new/text';
import { pdlStatusToCode } from '@/features/coverage/types/pdl-status';
import { PdlStatusBadge } from '@/features/drugs/components/pdl-status-badge';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useDelayedClose } from '@/hooks/use-delayed-close';
import { automatedPaDisplay, stepTherapyDisplay } from '@/utils/state-coverage';

type PaCriteriaDrawerProps = {
  reportItem?: TherapeuticClassReportItem;
  onClose: () => void;
}

export const PaCriteriaDrawer = ({ reportItem, onClose }: PaCriteriaDrawerProps) => {
  const { isOpen, handleOpen, handleClose } = useDelayedClose(!!reportItem, onClose);
  const { hasPolicies } = useCurrentUser();
  const isInternalUser = hasPolicies(['isAnyArtiaUser']);

  useEffect(() => {
    if (reportItem) {
      handleOpen();
    }
  }, [reportItem, handleOpen]);

  const getPaTypesString = (paTypes: Array<{ id: number; label: string }>) => {
    if (!paTypes || paTypes.length === 0) return 'None';
    return paTypes.map(pt => pt.label).join(', ');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return null;
    
    try {
      return DateTime.fromISO(dateString, { zone: 'utc' })
        .toLocal()
        .toLocaleString(DateTime.DATE_MED);
    } catch {
      return dateString;
    }
  };

  return (
    <Drawer open={isOpen} onClose={handleClose} size="3xl">
      {!!reportItem && (
        <>
          <DrawerHeader>
            <DrawerTitle>
              Prior Authorization Criteria Details
            </DrawerTitle>
            <Button plain onClick={handleClose}>
              <LiaTimesSolid/>
            </Button>
          </DrawerHeader>
          <DrawerBody>
            <div className="space-y-6">
              {/* Context Information */}
              {(reportItem.state?.label || reportItem.drug?.label) && (
                <div>
                  <SectionHeading level={3}>Context</SectionHeading>
                  <DescriptionList>
                    {reportItem.state?.label && (
                      <>
                        <DescriptionTerm>State</DescriptionTerm>
                        <DescriptionDetails>{reportItem.state.label}</DescriptionDetails>
                      </>
                    )}
                    
                    {reportItem.drug?.label && (
                      <>
                        <DescriptionTerm>Drug</DescriptionTerm>
                        <DescriptionDetails>{reportItem.drug.label}</DescriptionDetails>
                      </>
                    )}

                    <DescriptionTerm>PDL Status</DescriptionTerm>
                    <DescriptionDetails>
                      <PdlStatusBadge pdlStatus={pdlStatusToCode(reportItem.pdlStatus)}/>
                    </DescriptionDetails>

                    <DescriptionTerm>PDL Status Date</DescriptionTerm>
                    <DescriptionDetails>
                      <Nullish 
                        value={reportItem.pdlStatusDate}
                        valueRenderer={(date) => 
                          DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT)
                        }
                      />
                    </DescriptionDetails>
                  </DescriptionList>
                </div>
              )}

              {/* Prior Authorization Information */}
              <div>
                <SectionHeading level={3}>Prior Authorization</SectionHeading>
                <DescriptionList>
                  <DescriptionTerm>Type</DescriptionTerm>
                  <DescriptionDetails>
                    {getPaTypesString(reportItem.paTypes || [])}
                  </DescriptionDetails>

                  {isInternalUser && (
                    <>
                      <DescriptionTerm>Step Therapy</DescriptionTerm>
                      <DescriptionDetails>
                        <Nullish value={stepTherapyDisplay(reportItem.stepTherapyCount ?? undefined, reportItem.hasAdditionalSteps ?? undefined)}/>
                      </DescriptionDetails>
                    </>
                  )}

                  <DescriptionTerm>Source Link</DescriptionTerm>
                  <DescriptionDetails>
                    <Nullish
                      value={reportItem.paCriteriaSourceUrl}
                      valueRenderer={(link) => (
                        <TextLink 
                          href={link || ''} 
                          target="_blank" 
                          referrerPolicy="no-referrer"
                          className="break-all"
                        >
                          {link}
                        </TextLink>
                      )}
                    />
                  </DescriptionDetails>

                  <DescriptionTerm>Automated</DescriptionTerm>
                  <DescriptionDetails>
                    {automatedPaDisplay(reportItem.hasAutoPa)}
                  </DescriptionDetails>
                </DescriptionList>
              </div>

              {/* Prior Authorization Criteria Description */}
              <div>
                <SectionHeading level={3} className="mb-3">Prior Authorization Criteria</SectionHeading>
                {isEmpty(reportItem.paCriteriaDescription)
                  ? (<span className="text-sm text-gray-500">Additional criteria not specified.</span>)
                  : (<MarkdownPreview maxHeight="100%" markdown={reportItem.paCriteriaDescription || ''}/>)
                }
              </div>
            </div>
          </DrawerBody>
        </>
      )}
    </Drawer>
  );
};
